/**
 * UserHomepage: the logged-in account area shell, rendered at /account/*
 * (ProtectedRoute, requiresLogin=true, from app/App.tsx). Renders
 * components/Sidebar (bounties: create/listings/pursuits/submissions; settings:
 * profile/payments/notifications, plus privacy/terms footer links) beside nested
 * Routes: create (BountyCreateEdit), listings (+ :bountyId submissions pages),
 * pursuits (+ :bountyId BountyViewSubmit), submissions (+ :submissionId),
 * profile (Account), payments (PaymentsPage), notifications
 * (NotificationSettings). "/" redirects to /account/profile.
 */
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Account from "@/pages/user/Account";
import Notifications from "@/pages/user/NotificationSettings";
import BountyCreateEdit from "@/pages/bounty/BountyCreateEdit";
import BountyViewSubmit from "@/pages/bounty/BountyViewSubmit";
import ListingsPage from "@/pages/user/ListingsPage";
import PursuitsPage from "@/pages/user/PursuitsPage";
import BountySubmissionsPage from "@/pages/user/BountySubmissionsPage";
import SubmissionsPage from "@/pages/user/SubmissionsPage";
import SubmissionDisplay from "@/pages/submission/SubmissionDisplay";
import SubmissionBountyDisplay from "@/pages/submission/SubmissionBountyDisplay";
import { privacyPolicyLink, termsOfServiceLink } from "@/constants/links";
import PaymentsPage from "@/pages/user/PaymentsPage";
import BountyAcceptedSubmissionPage from "@/pages/user/BountyAcceptedSubmissionPage";
import { Sidebar, SidebarSection } from "@/components/Sidebar";

export const UserHomepage: React.FC = () => {
    // Transform the sidebar structure into the new format
    const sidebarSections: SidebarSection[] = [
        {
            title: "bounties",
            items: [
                { label: "create", url: "/account/create" },
                { label: "listings", url: "/account/listings" },
                { label: "pursuits", url: "/account/pursuits" },
                { label: "submissions", url: "/account/submissions" },
            ],
        },
        {
            title: "settings",
            items: [
                { label: "profile", url: "/account/profile" },
                { label: "payments", url: "/account/payments" },
                { label: "notifications", url: "/account/notifications" },
            ],
        },
    ];

    const sidebarFooter = (
        <div className="flex flex-col items-center space-y-1 text-xs text-black font-porsui">
            <a
                href={privacyPolicyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
            >
                privacy policy
            </a>
            <a
                href={termsOfServiceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
            >
                terms of service
            </a>
        </div>
    );

    return (
        <div className="bg-slate-50 h-full w-full flex text-black font-porsui relative">
            <Sidebar sections={sidebarSections} footer={sidebarFooter} />

            {/* Main Content */}
            <div className="flex-1 overflow-auto ml-0 md:ml-2 relative z-10">
                <Routes>
                    <Route path="/" element={<Navigate to="/account/profile" replace />} />
                    <Route
                        path="create"
                        element={<BountyCreateEdit mode="create" type="normal" />}
                    />
                    <Route path="listings" element={<ListingsPage />} />
                    <Route path="listings/:bountyId" element={<BountySubmissionsPage />} />
                    <Route
                        path="listings/:bountyId/submissions/:submissionId"
                        element={<SubmissionDisplay submissionType="normal" type="bounty" />}
                    />
                    <Route
                        path="listings/:bountyId/submissions/accepted"
                        element={<BountyAcceptedSubmissionPage />}
                    />
                    <Route path="pursuits" element={<PursuitsPage />} />
                    <Route path="pursuits/:bountyId" element={<BountyViewSubmit />} />
                    <Route path="submissions" element={<SubmissionsPage />} />
                    <Route path="submissions/:submissionId" element={<SubmissionBountyDisplay />} />
                    <Route path="profile" element={<Account />} />
                    <Route path="payments" element={<PaymentsPage />} />
                    <Route path="notifications" element={<Notifications />} />
                    {/* Fallback Route */}
                    <Route path="*" element={<Navigate to="account" replace />} />
                </Routes>
            </div>
        </div>
    );
};
